// src/views/PhoneDirectory.vue
<template>
  <div class="phone-directory-view container mt-3">
    <h1 class="mb-4">Annuaire Téléphonique Hôpitaux</h1>

    <!-- Filter/Search -->
    <div class="row mb-4">
       <div class="col-md-6">
            <label for="hospitalFilter" class="form-label">Filtrer par Hôpital</label>
            <select id="hospitalFilter" class="form-select" v-model="selectedHospital">
                <option value="">-- Tous les Hôpitaux --</option>
                <option v-for="hospital in uniqueHospitals" :key="hospital" :value="hospital">
                    {{ hospital }}
                </option>
            </select>
       </div>
        <div class="col-md-6">
            <label for="deptFilter" class="form-label">Rechercher Service/Numéro</label>
            <input type="search" id="deptFilter" class="form-control" v-model="searchTerm" placeholder="Nom service, numéro...">
        </div>
    </div>


    <!-- Loading / Error -->
    <div v-if="loading" class="d-flex justify-content-center my-4">
         <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Chargement...</span></div>
    </div>
     <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Contact List using Accordion -->
    <div v-else-if="filteredContacts.length > 0" class="accordion" id="phoneAccordion">
       <div v-for="(group, hospitalName) in groupedContacts" :key="hospitalName" class="accordion-item mb-2">
         <h2 class="accordion-header" :id="'heading-' + sanitizeId(hospitalName)">
           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" :data-bs-target="'#collapse-' + sanitizeId(hospitalName)" aria-expanded="false" :aria-controls="'collapse-' + sanitizeId(hospitalName)">
             {{ hospitalName }} ({{ group.length }})
           </button>
         </h2>
         <div :id="'collapse-' + sanitizeId(hospitalName)" class="accordion-collapse collapse" :aria-labelledby="'heading-' + sanitizeId(hospitalName)" data-bs-parent="#phoneAccordion">
           <div class="accordion-body p-0">
              <ul class="list-group list-group-flush">
                   <li v-for="contact in group" :key="contact.id || contact.phone_number" class="list-group-item d-flex justify-content-between align-items-center">
                     <div>
                        <strong class="d-block">{{ contact.department_name }}</strong>
                        <span class="text-muted small">{{ contact.notes || '' }}</span>
                     </div>
                     <a :href="'tel:' + contact.phone_number" class="btn btn-sm btn-outline-success">
                         <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="currentColor" class="me-1"><path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z"/></svg>
                         {{ contact.phone_number }}
                     </a>
                   </li>
              </ul>
           </div>
         </div>
       </div>
    </div>

    <div v-else class="text-center text-muted p-4 border rounded bg-light">
         Aucun contact trouvé pour "{{ searchTerm || selectedHospital || 'Tous' }}".
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';


const contacts = ref([]);
const loading = ref(false);
const error = ref('');
const searchTerm = ref('');
const selectedHospital = ref('');

const hardcodedContacts = [
  // La Rabta
  { id: 1, hospital_name: 'La Rabta', department_name: 'URGENCES UHCD HOSPITALISATION', phone_number: '99 060 524', notes: '' },
  { id: 2, hospital_name: 'La Rabta', department_name: 'URGENCES SAUV', phone_number: '92 876 803', notes: '' },
  { id: 3, hospital_name: 'La Rabta', department_name: 'ANESTHÉSIE - REA', phone_number: '99 060 855', notes: '' },
  { id: 4, hospital_name: 'La Rabta', department_name: 'BIOCHIMIE', phone_number: '99 061 270', notes: '' },
  { id: 5, hospital_name: 'La Rabta', department_name: 'CARDIOLOGIE', phone_number: '92 822 631', notes: '' },
  { id: 6, hospital_name: 'La Rabta', department_name: 'CARDIOLOGIE', phone_number: '99 061 912', notes: '' },
  { id: 7, hospital_name: 'La Rabta', department_name: 'CARDIOLOGIE PEDIATRIQUE', phone_number: '99 062 370', notes: '' },
  { id: 8, hospital_name: 'La Rabta', department_name: 'CCV', phone_number: '99 063 898', notes: '' },
  { id: 9, hospital_name: 'La Rabta', department_name: 'CHIRURGIE GÉNÉRALE A', phone_number: '99 060 534', notes: '' },
  { id: 10, hospital_name: 'La Rabta', department_name: 'CHIRURGIE GÉNÉRALE B', phone_number: '99 064 646', notes: '' },
  { id: 11, hospital_name: 'La Rabta', department_name: 'ENDOCRINOLOGIE', phone_number: '99 065 280', notes: '' },
  { id: 12, hospital_name: 'La Rabta', department_name: 'GASTROLOGIE A', phone_number: '99 060 478', notes: '' },
  { id: 13, hospital_name: 'La Rabta', department_name: 'GASTROLOGIE B', phone_number: '99 060 926', notes: '' },
  { id: 14, hospital_name: 'La Rabta', department_name: 'HÉMATOLOGIE', phone_number: '99 061 340', notes: '' },
  { id: 15, hospital_name: 'La Rabta', department_name: 'IMMUNOLOGIE', phone_number: '71 564 983', notes: '' },
  { id: 16, hospital_name: 'La Rabta', department_name: 'MALADIES INFECTIEUSES', phone_number: '99 060 623', notes: '' },
  { id: 17, hospital_name: 'La Rabta', department_name: 'MÉDECINE INTERNE', phone_number: '99 060 038', notes: '' },
  { id: 18, hospital_name: 'La Rabta', department_name: 'NEPHROLOGIE', phone_number: '99 060 934', notes: '' },
  { id: 19, hospital_name: 'La Rabta', department_name: 'ORL', phone_number: '99 061 554', notes: '' },
  { id: 20, hospital_name: 'La Rabta', department_name: 'ORTHOPÉDIE', phone_number: '99 062 122', notes: '' },
  { id: 21, hospital_name: 'La Rabta', department_name: 'PÉDIATRIE', phone_number: '99 063 722', notes: '' },
  { id: 22, hospital_name: 'La Rabta', department_name: 'PNEUMOLOGIE', phone_number: '99 064 136', notes: '' },
  { id: 23, hospital_name: 'La Rabta', department_name: 'RADIOLOGIE', phone_number: '99 064 984', notes: '' },
  { id: 24, hospital_name: 'La Rabta', department_name: 'RÉANIMATION', phone_number: '99 065 866', notes: '' },
  { id: 25, hospital_name: 'La Rabta', department_name: 'RHUMATOLOGIE', phone_number: '99 066 534', notes: '' },
  { id: 26, hospital_name: 'La Rabta', department_name: 'UROLOGIE', phone_number: '99 060 170', notes: '' },
  { id: 27, hospital_name: 'La Rabta', department_name: 'USIC', phone_number: '99 063 070', notes: '' },
  { id: 28, hospital_name: 'La Rabta', department_name: 'BOX SAUV', phone_number: '99 060 870', notes: '' },
  { id: 29, hospital_name: 'La Rabta', department_name: 'BOX URG CHIRURGIE', phone_number: '99 062 878', notes: '' },
  { id: 30, hospital_name: 'La Rabta', department_name: 'BOX URGENCES MEDECINE', phone_number: '99 061 480', notes: '' },
  { id: 31, hospital_name: 'La Rabta', department_name: 'PARASITO', phone_number: '99 712 209', notes: '' },

  // HOPITAL CHARLES NICOLE
  { id: 32, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'BACTÉRIO', phone_number: '71 564 765', notes: '' },
  { id: 33, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'CHIRURGIE A21', phone_number: '53 652 222', notes: '' },
  { id: 34, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'CHIRURGIE B23', phone_number: '58 363 211', notes: '' },
  { id: 35, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'CHIR. MAXILLO-FACIALE', phone_number: '71 578 045', notes: '' },
  { id: 36, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'CHIR. MAXILLO-FACIALE', phone_number: '71 578 047', notes: '' },
  { id: 37, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'ENDOCRINOLOGIE M8', phone_number: '58 363 560', notes: '' },
  { id: 38, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'GASTROLOGIE', phone_number: '58 363 662', notes: '' },
  { id: 39, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'GYNÉCOLOGIE B', phone_number: '92 023 995', notes: '' },
  { id: 40, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'IMMUNOLOGIE', phone_number: '71 578 055', notes: '' },
  { id: 41, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'NEPHROLOGIE M8 - SERVICE', phone_number: '58 360 370', notes: '' }, // Note: Duplicate department name, distinguishing based on phone
  { id: 42, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'NEPHROLOGIE M8 - SERVICE', phone_number: '58 350 805', notes: '' }, // Note: Duplicate department name, distinguishing based on phone
  { id: 43, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'MEDECINE INTERNE 10/4', phone_number: '58 355 959', notes: '' },
  { id: 44, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'NÉONATOLOGIE', phone_number: '97 657 514', notes: '' },
  { id: 45, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'NÉPHROLOGIE', phone_number: '94 445 789', notes: '' },
  { id: 46, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'NEUROLOGIE', phone_number: '55 306 800', notes: '' },
  { id: 47, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'ORL', phone_number: '58 350 850', notes: '' },
  { id: 48, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'ORTHOPÉDIE', phone_number: '94 445 743', notes: '' },
  { id: 49, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'PNEUMOLOGIE', phone_number: '58 350 858', notes: '' },
  { id: 50, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RADIOLOGIE', phone_number: '94 445 792', notes: '' },
  { id: 51, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RADIOLOGIE', phone_number: '71 578 262', notes: '' },
  { id: 52, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RADIOLOGIE - IRM', phone_number: '71 571 326', notes: '' },
  { id: 53, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RADIOLOGIE - IRM', phone_number: '71 578 476', notes: '' },
  { id: 54, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RÉANIMATION', phone_number: '71 578 828', notes: '' },
  { id: 55, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'RÉANIMATION', phone_number: '71 578 341', notes: '' },
  { id: 56, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'CARDIOLOGIE', phone_number: '55 000 679', notes: '' },
  { id: 57, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'MEDECINE DU TRAVAIL', phone_number: '58 745 739', notes: '' },
  { id: 58, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'URGENCES', phone_number: '99 062 078', notes: '' },
  { id: 59, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'UROLOGIE', phone_number: '58 360 166', notes: '' },
  { id: 60, hospital_name: 'HOPITAL CHARLES NICOLE', department_name: 'VIROLOGIE', phone_number: '71 578 186', notes: '' },

  // CTGB
  { id: 61, hospital_name: 'CTGB', department_name: 'ANESTHÉSIE-RÉA', phone_number: '93 200 438', notes: '' },
  { id: 62, hospital_name: 'CTGB', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '93 200 435', notes: '' },
  { id: 63, hospital_name: 'CTGB', department_name: 'NEUROCHIRURGIE', phone_number: '93 200 436', notes: '' },
  { id: 64, hospital_name: 'CTGB', department_name: 'ORTHOPÉDIE', phone_number: '93 200 434', notes: '' },

  // INN
  { id: 65, hospital_name: 'INN', department_name: 'ANESTHÉSIE-RÉA', phone_number: '99 926 298', notes: '' },
  { id: 66, hospital_name: 'INN', department_name: 'ANESTHÉSIE-RÉA', phone_number: '99 062 878', notes: '' }, // Duplicate department, likely different line/person
  { id: 67, hospital_name: 'INN', department_name: 'NEUROCHIRURGIE', phone_number: '99 926 068', notes: '' },
  { id: 68, hospital_name: 'INN', department_name: 'NEUROLOGIE ADULTES', phone_number: '99 925 149', notes: '' },
  { id: 69, hospital_name: 'INN', department_name: 'RADIOLOGIE', phone_number: '99 926 388', notes: '' },
  { id: 70, hospital_name: 'INN', department_name: 'RADIOLOGIE - IRM', phone_number: '71 151 066', notes: '' },

  // HOPITAL HABIB THAMEUR
  { id: 71, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'ANESTHÉSIE-RÉA', phone_number: '93 506 694', notes: '' },
  { id: 72, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'CARDIOLOGIE', phone_number: '93 506 814', notes: '' },
  { id: 73, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'ORL', phone_number: '93 506 786', notes: '' },
  { id: 74, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '93 506 712', notes: '' },
  { id: 75, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '93 506 716', notes: '' },
  { id: 76, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'CHIRURGIE PÉDIATRIQUE', phone_number: '93 506 659', notes: '' },
  { id: 77, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'DERMATOLOGIE', phone_number: '93 506 725', notes: '' },
  { id: 78, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'GASTROLOGIE', phone_number: '93 506 812', notes: '' },
  { id: 79, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'MÉDECINE INTERNE', phone_number: '93 506 726', notes: '' },
  { id: 80, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'OPHTALMOLOGIE', phone_number: '93 506 765', notes: '' },
  { id: 81, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'RADIOLOGIE', phone_number: '93 506 655', notes: '' },
  { id: 82, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'URGENCES', phone_number: '93 506 415', notes: '' },
  { id: 83, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'UROLOGIE', phone_number: '93 507 360', notes: '' },
  { id: 84, hospital_name: 'HOPITAL HABIB THAMEUR', department_name: 'PHARMACIE', phone_number: '93 507 038', notes: '' },

  // HOPITAL ABDERRAHMEN MAMI
  { id: 85, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'BACTÉRIOLOGIE', phone_number: '96 930 001', notes: '' },
  { id: 86, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'CARDIOLOGIE', phone_number: '92 735 067', notes: '' },
  { id: 87, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'CCV', phone_number: '92 735 064', notes: '' },
  { id: 88, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'ONCOLOGIE', phone_number: '92 735 158', notes: '' },
  { id: 89, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON 1', phone_number: '96 929 947', notes: '' },
  { id: 90, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON 2', phone_number: '96 929 949', notes: '' },
  { id: 91, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON 3', phone_number: '96 929 947', notes: '' }, // Note: Same number as Pavillon 1
  { id: 92, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON 4', phone_number: '96 929 906', notes: '' },
  { id: 93, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON B', phone_number: '96 929 995', notes: '' },
  { id: 94, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON C', phone_number: '96 929 998', notes: '' },
  { id: 95, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'PNEUMOLOGIE PAVILLON D', phone_number: '96 929 946', notes: '' },
  { id: 96, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'RADIOLOGIE', phone_number: '96 930 811', notes: '' },
  { id: 97, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'RÉANIMATION', phone_number: '96 930 044', notes: '' },
  { id: 98, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'RÉANIMATION', phone_number: '92 735 176', notes: '' },
  { id: 99, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'RÉANIMATION', phone_number: '93 506 804', notes: '' },
  { id: 100, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'URGENCES', phone_number: '96 930 013', notes: '' },
  { id: 101, hospital_name: 'HOPITAL ABDERRAHMEN MAMI', department_name: 'URG AMB', phone_number: '98 925 093', notes: '' },

  // HOPITAL MAHMOUD AL MATRI
  { id: 102, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'URGENCES', phone_number: '99 029 824', notes: '' },
  { id: 103, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'URGENCES', phone_number: '99 029 844', notes: '' },
  { id: 104, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '94 224 915', notes: '' },
  { id: 105, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'MATERNITÉ', phone_number: '92 735 307', notes: '' },
  { id: 106, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'GASTRO', phone_number: '94 225 780', notes: '' },
  { id: 107, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'DIALYSE', phone_number: '94 225 261', notes: '' },
  { id: 108, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'SURVEILLANT APRES MIDI', phone_number: '98 568 029', notes: '' },
  { id: 109, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'SURVEILLANT NUIT', phone_number: '94 091 221', notes: '' },
  { id: 110, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'SURVEILLANT NUIT', phone_number: '94 224 915', notes: '' },
  { id: 111, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'SURVEILLANT NUIT', phone_number: '50 398 733', notes: '' },
  { id: 112, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'SERVICE INFORMATIQUE', phone_number: '93 675 798', notes: '' },
  { id: 113, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'MAINTENANCE', phone_number: '20 530 342', notes: '' },
  { id: 114, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'MAINTENANCE', phone_number: '97 806 800', notes: '' },
  { id: 115, hospital_name: 'HOPITAL MAHMOUD AL MATRI', department_name: 'MAINTENANCE', phone_number: '99 032 179', notes: '' },

  // HOPITAL MILITAIRE DE TUNIS
  { id: 116, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'ANESTHÉSIE-RÉA', phone_number: '95 979 255', notes: '' },
  { id: 117, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'NEUROCHIRURGIE', phone_number: '95 978 915', notes: '' },
  { id: 118, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'CCV', phone_number: '95 978 942', notes: '' },
  { id: 119, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'CHIRURGIE GENERALE', phone_number: '95 978 956', notes: '' },
  { id: 120, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'CHIRURGIE CARDIO-THORACIQUE', phone_number: '95 978 963', notes: '' },
  { id: 121, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'ORTHOPÉDIE', phone_number: '95 978 973', notes: '' },
  { id: 122, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'ORL', phone_number: '95 978 980', notes: '' },
  { id: 123, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'CARDIOLOGIE', phone_number: '95 978 990', notes: '' },
  { id: 124, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'GYNÉCOLOGIE', phone_number: '95 978 993', notes: '' },
  { id: 125, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'UROLOGIE', phone_number: '95 979 001', notes: '' },
  { id: 126, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'RÉANIMATION MÉDICALE', phone_number: '95 979 002', notes: '' },
  { id: 127, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'MÉDECINE INTERNE / RHUMATO', phone_number: '95 979 005', notes: '' },
  // { id: 128, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'GASTRO-ENTÉROLOGIE', phone_number: '', notes: '' }, // No phone number listed
  { id: 129, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'PNEUMOLOGIE', phone_number: '95 979 025', notes: '' },
  { id: 130, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'NEUROLOGIE', phone_number: '95 979 026', notes: '' },
  { id: 131, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'NÉONATOLOGIE', phone_number: '95 979 045', notes: '' },
  { id: 132, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'PÉDIATIRE', phone_number: '95 979 100', notes: '' },
  { id: 133, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'HÉMATOLOGIE CLINIQUE', phone_number: '95 979 046', notes: '' },
  { id: 134, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'URGENCES', phone_number: '95 979 076', notes: '' },
  { id: 135, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'RADIOLOGIE', phone_number: '95 979 166', notes: '' },
  { id: 136, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'BIOCHIMIE', phone_number: '95 979 170', notes: '' },
  { id: 137, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'HÉMATOLOGIE BIOLOGIQUE', phone_number: '95 979 179', notes: '' },
  { id: 138, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'NÉPHROLOGIE', phone_number: '97 120 079', notes: '' },
  { id: 139, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'PHARMACIE', phone_number: '95 979 195', notes: '' },
  { id: 140, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'CHIRURGIE DE LA MAIN', phone_number: '95 462 748', notes: '' },
  { id: 141, hospital_name: 'HOPITAL MILITAIRE DE TUNIS', department_name: 'STANDARD', phone_number: '71 391 133', notes: '' },

  // HOPITAL D'ENFANTS
  { id: 142, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'ANESTHÉSIE-RÉA', phone_number: '99 944 884', notes: '' },
  { id: 143, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'ANESTHÉSIE-RÉA', phone_number: '71 151 758', notes: '' },
  { id: 144, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'HEMATOLOGIE BIOLOGIQUE', phone_number: '99 945 118', notes: '' },
  { id: 145, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'CHIRURGIE PÉDIATRIQUE A', phone_number: '99 944 534', notes: '' },
  { id: 146, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'CHIRURGIE PÉDIATRIQUE B', phone_number: '99 945 492', notes: '' },
  { id: 147, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'ORTHOPÉDIE PÉDIATRIQUE', phone_number: '99 944 562', notes: '' },
  { id: 148, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'PÉDIATRIE D (PUC)', phone_number: '99 944 634', notes: '' },
  { id: 149, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'PÉDIATRIE 2EME ÉTAGE', phone_number: '99 945 074', notes: '' },
  { id: 150, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'PÉDIATRIE 3ÈME ÉTAGE', phone_number: '99 945 380', notes: '' },
  { id: 151, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'PÉDIATRIE 4ÈME ÉTAGE', phone_number: '99 945 460', notes: '' },
  { id: 152, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'RADIOLOGIE', phone_number: '99 944 516', notes: '' },
  { id: 153, hospital_name: 'HOPITAL D\'ENFANTS', department_name: 'RÉA POLY', phone_number: '99 944 762', notes: '' },

  // HOPITAL MONGI SLIM
  { id: 154, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'CARDIOLOGIE', phone_number: '94 827 706', notes: '' },
  { id: 155, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'CHIRURGIE', phone_number: '94 827 603', notes: '' },
  { id: 156, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'RADIOLOGIE', phone_number: '94 827 607', notes: '' },
  { id: 157, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'RÉANIMATION', phone_number: '94 827 676', notes: '' },
  { id: 158, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'URGENCES', phone_number: '94 827 719', notes: '' },
  { id: 159, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'NÉPHROLOGIE', phone_number: '96 347 148', notes: '' },
  { id: 160, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'GASTROLOGIE', phone_number: '92 103 683', notes: '' },
  { id: 161, hospital_name: 'HOPITAL MONGI SLIM', department_name: 'PNEUMOLOGIE', phone_number: '97 105 821', notes: '' },

  // HOPITAL AZIZA OTHMANA
  { id: 162, hospital_name: 'HOPITAL AZIZA OTHMANA', department_name: 'HÉMATOLOGIE CLINIQUE', phone_number: '98 108 134', notes: '' },
  { id: 163, hospital_name: 'HOPITAL AZIZA OTHMANA', department_name: 'STANDARD', phone_number: '71 151 300', notes: '' },

  // CMNT
  { id: 164, hospital_name: 'CMNT', department_name: 'GYNÉCOLOGIE C', phone_number: '71 565 578', notes: '' },
  { id: 165, hospital_name: 'CMNT', department_name: 'NÉONATOLOGIE', phone_number: '99 940 636', notes: '' },
  { id: 166, hospital_name: 'CMNT', department_name: 'NÉONATOLOGIE', phone_number: '99 945 504', notes: '' },
  { id: 167, hospital_name: 'CMNT', department_name: 'URGENCES GYNÉCOLOGIQUES', phone_number: '99 941 268', notes: '' },

  // HOPITAL REGIONAL BEN AROUS
  { id: 168, hospital_name: 'HOPITAL REGIONAL BEN AROUS', department_name: 'RADIOLOGIE', phone_number: '20 097 570', notes: '' },
  { id: 169, hospital_name: 'HOPITAL REGIONAL BEN AROUS', department_name: 'RÉANIMATION', phone_number: '56 752 360', notes: '' },
  { id: 170, hospital_name: 'HOPITAL REGIONAL BEN AROUS', department_name: 'URGENCES', phone_number: '58 443 713', notes: '' },

  // HOPITAL RAZI
  { id: 171, hospital_name: 'HOPITAL RAZI', department_name: 'NEUROLOGIE', phone_number: '80 101 915', notes: '' },
  { id: 172, hospital_name: 'HOPITAL RAZI', department_name: 'STANDARD', phone_number: '71 600 339', notes: '' },
  { id: 173, hospital_name: 'HOPITAL RAZI', department_name: 'STANDARD', phone_number: '70 162 200', notes: '' },

  // INSTITUT KASSAB
  { id: 174, hospital_name: 'INSTITUT KASSAB', department_name: '', phone_number: '71 606 914', notes: '' }, // Department name missing in HTML

  // CHOC ROOM
  { id: 175, hospital_name: 'CHOC ROOM', department_name: '', phone_number: '71 567 500', notes: '' }, // Department name missing in HTML
  { id: 176, hospital_name: 'CHOC ROOM', department_name: '', phone_number: '71 570 300', notes: '' },
  { id: 177, hospital_name: 'CHOC ROOM', department_name: '', phone_number: '80 103 160', notes: '' },

  // CNGMO
  { id: 178, hospital_name: 'CNGMO', department_name: 'CENTRE NATIONAL DE GREFFE DE LA MOELLE OSSEUSE', phone_number: '71 565 428', notes: '' },

  // CNPTO
  { id: 179, hospital_name: 'CNPTO', department_name: 'CENTRE NATIONAL POUR LA PROMOTION DE LA TRANSPLANTATION D\'ORGANES', phone_number: '23 061 515', notes: '' },

  // CENTRE NATIONAL DE TRANSFUSION SANGUINE
  { id: 180, hospital_name: 'CENTRE NATIONAL DE TRANSFUSION SANGUINE', department_name: 'COUPOLE', phone_number: '98 724 946', notes: '' },
  { id: 181, hospital_name: 'CENTRE NATIONAL DE TRANSFUSION SANGUINE', department_name: 'COUPOLE', phone_number: '98 724 947', notes: '' },
  { id: 182, hospital_name: 'CENTRE NATIONAL DE TRANSFUSION SANGUINE', department_name: 'COUPOLE', phone_number: '71 573 948', notes: '' },

  // INSTITUT NUTRITION
  { id: 183, hospital_name: 'INSTITUT NUTRITION', department_name: '', phone_number: '94 074 054', notes: '' }, // Department name missing in HTML

  // PHARMACO VIGILANCE
  { id: 184, hospital_name: 'PHARMACO VIGILANCE', department_name: '', phone_number: '50 207 577', notes: '' }, // Department name missing in HTML
  { id: 185, hospital_name: 'PHARMACO VIGILANCE', department_name: '', phone_number: '92 615 666', notes: '' },
  { id: 186, hospital_name: 'PHARMACO VIGILANCE', department_name: '', phone_number: '71 260 699', notes: '' },

  // CAMU
  { id: 187, hospital_name: 'CAMU', department_name: 'REANIMATION MEDICALE', phone_number: '71 249 029', notes: '' },
  { id: 188, hospital_name: 'CAMU', department_name: 'REANIMATION MEDICALE', phone_number: '71 249 014', notes: '' },

  // INSTITUT SALAH AZAIEZ
  { id: 189, hospital_name: 'INSTITUT SALAH AZAIEZ', department_name: 'CHIRURGIE CARCINOLOGIQUE', phone_number: '95 890 630', notes: '' },
  { id: 190, hospital_name: 'INSTITUT SALAH AZAIEZ', department_name: 'STANDARD', phone_number: '71 570 295', notes: '' },
  { id: 191, hospital_name: 'INSTITUT SALAH AZAIEZ', department_name: 'STANDARD', phone_number: '71 150 700', notes: '' },

  // ONMNE
  { id: 192, hospital_name: 'ONMNE', department_name: '', phone_number: '71 284 547', notes: '' }, // Department name missing in HTML
  { id: 193, hospital_name: 'ONMNE', department_name: '', phone_number: '71 284 560', notes: '' },
  { id: 194, hospital_name: 'ONMNE', department_name: '', phone_number: '71 284 561', notes: '' },

  // HOPITAL DE JENDOUBA
  { id: 195, hospital_name: 'HOPITAL DE JENDOUBA', department_name: 'STANDARD', phone_number: '78 609 946', notes: '' },

  // HOPITAL DE MJEZ EL BEB
  { id: 196, hospital_name: 'HOPITAL DE MJEZ EL BEB', department_name: 'STANDARD', phone_number: '78 560 600', notes: '' },
  { id: 197, hospital_name: 'HOPITAL DE MJEZ EL BEB', department_name: 'STANDARD', phone_number: '78 560 010', notes: '' },

  // HOPITAL DE MENZEL BOURGUIBA
  { id: 198, hospital_name: 'HOPITAL DE MENZEL BOURGUIBA', department_name: 'STANDARD', phone_number: '72 113 400', notes: '' },

  // HÔPITAL RÉGIONAL TAHER MAAMOURI NABEUL
  { id: 199, hospital_name: 'HÔPITAL RÉGIONAL TAHER MAAMOURI NABEUL', department_name: 'STANDARD', phone_number: '72 100 500', notes: '' },
  { id: 200, hospital_name: 'HÔPITAL RÉGIONAL TAHER MAAMOURI NABEUL', department_name: 'STANDARD', phone_number: '72 271 793', notes: '' },
  { id: 201, hospital_name: 'HÔPITAL RÉGIONAL TAHER MAAMOURI NABEUL', department_name: 'STANDARD', phone_number: '72 237 198', notes: '' },

  // HOPITAL DE SLIMEN
  { id: 202, hospital_name: 'HOPITAL DE SLIMEN', department_name: 'STANDARD', phone_number: '72 331 511', notes: '' },

  // HÔPITAL DE BIZERTE
  { id: 203, hospital_name: 'HÔPITAL DE BIZERTE', department_name: 'STANDARD', phone_number: '72 110 200', notes: '' },
  { id: 204, hospital_name: 'HÔPITAL DE BIZERTE', department_name: 'ORTHOPÉDIE', phone_number: '93 500 028', notes: '' },

  // HOPITAUX DE SFAX
  { id: 205, hospital_name: 'HOPITAUX DE SFAX', department_name: 'CARDIOLOGIE', phone_number: '55 765 371', notes: '' },
  { id: 206, hospital_name: 'HOPITAUX DE SFAX', department_name: 'CHIRURGIE CARDIOVASCULAIRE', phone_number: '54 378 481', notes: '' },
  { id: 207, hospital_name: 'HOPITAUX DE SFAX', department_name: 'CHIRURGIE MAXILLO-FACIALE', phone_number: '25 427 602', notes: '' },
  { id: 208, hospital_name: 'HOPITAUX DE SFAX', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '54 696 372', notes: '' },
  { id: 209, hospital_name: 'HOPITAUX DE SFAX', department_name: 'ENDOCRINOLOGIE', phone_number: '55 766 053', notes: '' },
  { id: 210, hospital_name: 'HOPITAUX DE SFAX', department_name: 'GASTROLOGIE', phone_number: '55 765 188', notes: '' },
  { id: 211, hospital_name: 'HOPITAUX DE SFAX', department_name: 'HÉMATOLOGIE', phone_number: '55 765 118', notes: '' },
  { id: 212, hospital_name: 'HOPITAUX DE SFAX', department_name: 'GYNÉCOLOGIE', phone_number: '55 765 118', notes: '' }, // Note: Same number as Hématologie
  { id: 213, hospital_name: 'HOPITAUX DE SFAX', department_name: 'MALADIES INFECTIEUSES', phone_number: '55 765 115', notes: '' },
  { id: 214, hospital_name: 'HOPITAUX DE SFAX', department_name: 'MÉDECINE INTERNE', phone_number: '55 766 123', notes: '' },
  { id: 215, hospital_name: 'HOPITAUX DE SFAX', department_name: 'NEUROLOGIE', phone_number: '56 792 515', notes: '' },
  { id: 216, hospital_name: 'HOPITAUX DE SFAX', department_name: 'NÉPHROLOGIE', phone_number: '51 743 512', notes: '' },
  { id: 217, hospital_name: 'HOPITAUX DE SFAX', department_name: 'NEUROCHIRURGIE', phone_number: '55 454 346', notes: '' },
  { id: 218, hospital_name: 'HOPITAUX DE SFAX', department_name: 'NEUROPÉOIATRIE', phone_number: '55 765 534', notes: '' },
  { id: 219, hospital_name: 'HOPITAUX DE SFAX', department_name: 'PNEUMOLOGIE', phone_number: '55 765 183', notes: '' },
  { id: 220, hospital_name: 'HOPITAUX DE SFAX', department_name: 'RADIOLOGIE HEDI CHAKEUR', phone_number: '55 765 216', notes: '' },
  { id: 221, hospital_name: 'HOPITAUX DE SFAX', department_name: 'SAMU : DÉCHOCAGE', phone_number: '56 783 120', notes: '' },

  // Hôpital les aghlabites kairouan
  { id: 222, hospital_name: 'Hôpital les aghlabites kairouan', department_name: 'STANDARD', phone_number: '77 800 644', notes: '' },

  // Hôpital Ibn jazzar kairouan
  { id: 223, hospital_name: 'Hôpital Ibn jazzar kairouan', department_name: 'STANDARD', phone_number: '77 801 100', notes: '' },

  // Hôpital universitaire Sahloul sousse
  { id: 224, hospital_name: 'Hôpital universitaire Sahloul sousse', department_name: 'STANDARD', phone_number: '73 101 300', notes: '' },

  // Hôpital Taher Sfar Mahdia
  { id: 225, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'CHIRURGIE GÉNÉRALE', phone_number: '95 232 080', notes: '' },
  { id: 226, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'CARDIOLOGIE', phone_number: '95 232 088', notes: '' },
  { id: 227, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'ORTHOPÉDIE', phone_number: '95 232 090', notes: '' },
  { id: 228, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'ANESTHÉSIE-RÉA', phone_number: '95 232 099', notes: '' },
  { id: 229, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'RÉANIMATION MÉDICALE', phone_number: '95 232 201', notes: '' },
  { id: 230, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'GYNÉCOLOGIE', phone_number: '95 232 203', notes: '' },
  { id: 231, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'GYNÉCOLOGIE', phone_number: '99 533 565', notes: '' },
  { id: 232, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'RADIOLOGIE', phone_number: '95 232 205', notes: '' },
  { id: 233, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'OPHTALMOLOGIE', phone_number: '95 232 208', notes: '' },
  { id: 234, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'ORL', phone_number: '95 232 332', notes: '' },
  { id: 235, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'NÉONATOLOGIE', phone_number: '95 232 382', notes: '' },
  { id: 236, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'NÉONATOLOGIE', phone_number: '95 232 282', notes: '' },
  { id: 237, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'URGENCES', phone_number: '95 232 308', notes: '' },
  { id: 238, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'SMUR', phone_number: '95 232 310', notes: '' },
  { id: 239, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'UROLOGIE', phone_number: '95 234 570', notes: '' },
  { id: 240, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'PÉDIATRIE', phone_number: '95 232 409', notes: '' },
  { id: 241, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'DIALYSE', phone_number: '95 232 426', notes: '' },
  { id: 242, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'MÉDECINE INTERNE', phone_number: '95 232 430', notes: '' },
  { id: 243, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'PSYCHIATRIE', phone_number: '95 232 490', notes: '' },
  { id: 244, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'PNEUMOLOGIE', phone_number: '95 232 495', notes: '' },
  { id: 245, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'RHUMATOLOGIE', phone_number: '95 231 497', notes: '' },
  { id: 246, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'NEUROLOGIE', phone_number: '95 231 501', notes: '' },
  { id: 247, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'LABORATOIRE BIOLOGIE', phone_number: '95 231 701', notes: '' },
  { id: 248, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'BACTÉRIOLOGIE', phone_number: '95 231 707', notes: '' },
  { id: 249, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'PHARMACIE', phone_number: '95 232 136', notes: '' },
  { id: 250, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'ADMINISTRATION DE GARDE', phone_number: '95 232 633', notes: '' },
  { id: 251, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'DIRECTEUR', phone_number: '90 252 475', notes: '' },
  { id: 252, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'MÉDECINE DU TRAVAIL', phone_number: '95 230 035', notes: '' },
  { id: 253, hospital_name: 'Hôpital Taher Sfar Mahdia', department_name: 'MÉDECINE', phone_number: '95 232 450', notes: '' },

  // Hôpital régional kasserine
  { id: 254, hospital_name: 'Hôpital régional kasserine', department_name: 'STANDARD', phone_number: '77 109 000', notes: '' },

  // HÔPITAL RÉGIONAL DE ZAGHOUAN
  { id: 255, hospital_name: 'HÔPITAL RÉGIONAL DE ZAGHOUAN', department_name: 'RÉANIMATION MÉDICALE', phone_number: '58 404 928', notes: '' },

  // HÔPITAL BOUGATFA DE BIZERTE
  { id: 256, hospital_name: 'HÔPITAL BOUGATFA DE BIZERTE', department_name: 'STANDARD', phone_number: '72 110 200', notes: '' },

  // HÔPITAL RÉGIONAL MENZEL BOURGUIBA
  { id: 257, hospital_name: 'HÔPITAL RÉGIONAL MENZEL BOURGUIBA', department_name: 'STANDARD', phone_number: '72 113 400', notes: '' },

  // CHU MEDENINE
  { id: 258, hospital_name: 'CHU MEDENINE', department_name: 'REANIMATION', phone_number: '25 010 153', notes: '' },
  { id: 259, hospital_name: 'CHU MEDENINE', department_name: 'CARDIO', phone_number: '52 831 150', notes: '' },
  { id: 260, hospital_name: 'CHU MEDENINE', department_name: 'STANDARD', phone_number: '75 640 044', notes: '' },

  // Délégués à la Protection de l'Enfance (DPE)
  { id: 261, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'TUNIS', phone_number: '71 905 156', notes: '' },
  { id: 262, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'ARIANA', phone_number: '70 730 664', notes: '' },
  { id: 263, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'MANOUBA', phone_number: '71 603 568', notes: '' },
  { id: 264, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'BEN AROUS', phone_number: '71 395 383', notes: '' },
  { id: 265, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'NABEUL', phone_number: '72 224 248', notes: '' },
  { id: 266, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'ZAGHOUAN', phone_number: '72 681 108', notes: '' },
  { id: 267, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'BIZERTE', phone_number: '72 422 455', notes: '' },
  { id: 268, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'SILIANA', phone_number: '78 872 840', notes: '' },
  { id: 269, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'BEJA', phone_number: '78 452 611', notes: '' },
  { id: 270, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'JENDOUBA', phone_number: '78 607 288', notes: '' },
  { id: 271, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'LE KEF', phone_number: '78 227 800', notes: '' },
  { id: 272, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'MAHDIA', phone_number: '73 693 012', notes: '' },
  { id: 273, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'MONASTIR', phone_number: '73 463 133', notes: '' },
  { id: 274, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'SOUSSE', phone_number: '73 382 178', notes: '' },
  { id: 275, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'SFAX', phone_number: '74 402 166', notes: '' },
  { id: 276, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'KASSERINE', phone_number: '77 470 281', notes: '' },
  { id: 277, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'KAIROUAN', phone_number: '77 237 500', notes: '' },
  { id: 278, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'SIDI BOUZID', phone_number: '76 622 450', notes: '' },
  { id: 279, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'MEDENINE', phone_number: '75 647 523', notes: '' },
  { id: 280, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'GABES', phone_number: '75 275 852', notes: '' },
  { id: 281, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'TATAOUINE', phone_number: '75 852 635', notes: '' },
  { id: 282, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'GAFSA', phone_number: '76 227 544', notes: '' },
  { id: 283, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'TOZEUR', phone_number: '76 477 013', notes: '' },
  { id: 284, hospital_name: 'Délégués à la Protection de l\'Enfance (DPE)', department_name: 'KEBILI', phone_number: '75 493 260', notes: '' },

  // Lutte contre violence à l'égard des femmes
  { id: 285, hospital_name: 'Lutte contre violence à l\'égard des femmes', department_name: '', phone_number: '80 101 030', notes: '' }, // Department name missing in HTML
];

const fetchContacts = async () => {
  // Replace this with Supabase fetch if using DB
  loading.value = true; error.value = '';
  try {
    // Simulate fetch delay
    await new Promise(resolve => setTimeout(resolve, 300));
    // // --- Supabase Fetch Logic (if using DB) ---
    // const { data, error: dbError } = await supabase.functions.invoke('get-contacts');
    // // OR direct fetch if RLS allows:
    // // const { data, error: dbError } = await supabase.from('department_contacts').select('*').order('hospital_name').order('department_name');
    // if (dbError) throw dbError;
    // contacts.value = data || [];
    // --- End Supabase Fetch ---

    // --- Use Hardcoded Data ---
     contacts.value = hardcodedContacts;
    // --- End Use Hardcoded Data ---

  } catch (err) {
    console.error("Erreur fetchContacts:", err);
    error.value = `Impossible de charger l'annuaire: ${err.message || err}`;
    contacts.value = [];
  } finally {
    loading.value = false;
  }
};

const uniqueHospitals = computed(() => {
  const names = contacts.value.map(c => c.hospital_name);
  return [...new Set(names)].sort(); // Get unique names and sort
});

const filteredContacts = computed(() => {
    let results = contacts.value;
    const searchLower = searchTerm.value.toLowerCase();

    // Filter by selected hospital
    if (selectedHospital.value) {
        results = results.filter(c => c.hospital_name === selectedHospital.value);
    }

    // Filter by search term (on department, number, notes)
    if (searchLower) {
         results = results.filter(c =>
            c.department_name?.toLowerCase().includes(searchLower) ||
            c.phone_number?.toLowerCase().includes(searchLower) ||
            c.notes?.toLowerCase().includes(searchLower)
         );
    }
    return results;
});

// Group contacts by hospital for accordion display
const groupedContacts = computed(() => {
    return filteredContacts.value.reduce((acc, contact) => {
        const hospital = contact.hospital_name;
        if (!acc[hospital]) {
            acc[hospital] = [];
        }
        acc[hospital].push(contact);
        return acc;
    }, {});
});

// Helper to create safe IDs for accordion controls
const sanitizeId = (text) => {
    if (!text) return 'id-' + Math.random().toString(36).substr(2, 9);
    return text.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
};

onMounted(fetchContacts);

</script>

<style scoped>
.phone-directory-view {
    /* Container styles inherited */
}
 .accordion-button { /* Bootstrap accordion button */
    font-weight: 500;
    font-size: 1.1rem;
 }
 .accordion-button:not(.collapsed) {
    background-color: rgba(var(--primary-color-rgb), 0.1);
    color: var(--primary-color-dark);
 }
 .accordion-body {
     padding: 0; /* Remove default padding */
 }
.list-group-item {
    border-left: 0;
    border-right: 0;
    border-radius: 0; /* Flush look */
}
.list-group-item:last-child {
    border-bottom: 0;
}
.list-group-item strong {
     color: var(--text-color);
 }
.list-group-item .btn svg {
    width: 0.8em; height: 0.8em; margin-top: -2px;
}
/* Dark mode specific styles for accordion if needed */
 [data-theme="dark"] .accordion-button {
    background-color: var(--component-background-alt);
    color: var(--text-color);
 }
  [data-theme="dark"] .accordion-button:not(.collapsed) {
    background-color: rgba(var(--primary-color-rgb), 0.2);
    color: var(--primary-color);
 }
  [data-theme="dark"] .accordion-item {
    border-color: var(--border-color-light);
 }
  [data-theme="dark"] .list-group-item {
     background-color: var(--component-background);
     border-color: var(--border-color-light);
  }
   [data-theme="dark"] .list-group-item strong {
     color: var(--text-color);
 }

</style>